import { t } from '../../i18n';
// src/components/ads/AdList.jsx
import React, { useMemo } from 'react';
import { computeRuntimeStatus } from './adsStorage';
function Badge({ tone = 'gray', children }) {
    const cls = tone === 'green'
        ? 'bg-green-50 text-green-700 border-green-200'
        : tone === 'amber'
            ? 'bg-amber-50 text-amber-700 border-amber-200'
            : tone === 'red'
                ? 'bg-red-50 text-red-700 border-red-200'
                : 'bg-gray-100 text-gray-700 border-gray-200';
    return <span className={`text-xs px-2 py-0.5 rounded border ${cls}`}>{children}</span>;
}
function statusTone(st) {
    if (st === 'ACTIVE')
        return 'green';
    if (st === 'SCHEDULED')
        return 'amber';
    if (st === 'PENDING')
        return 'amber';
    if (st === 'EXPIRED')
        return 'red';
    if (st === 'DISABLED')
        return 'gray';
    return 'gray';
}
function statusLabel(st) {
    if (st === 'ACTIVE')
        return t("\uB178\uCD9C\uC911");
    if (st === 'SCHEDULED')
        return t("\uC608\uC57D");
    if (st === 'PENDING')
        return t("\uC608\uC57D \uBCF4\uB958");
    if (st === 'EXPIRED')
        return t("\uB9CC\uB8CC");
    if (st === 'DISABLED')
        return t("\uBE44\uD65C\uC131");
    return st;
}
function fmtDT(v) {
    if (!v)
        return '-';
    try {
        const d = new Date(String(v).replace(' ', 'T'));
        if (Number.isNaN(d.getTime()))
            return v;
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
    catch {
        return v;
    }
}
function pickThumb(ad) {
    return ad?.imageUrl || null;
}
function AdCard({ ad, active, onSelect, onEdit, onDelete, onMoveUp, onMoveDown }) {
    const st = useMemo(() => computeRuntimeStatus(ad), [ad]);
    const thumb = useMemo(() => pickThumb(ad), [ad]);
    return (<button onClick={() => onSelect(ad.id)} className={[
            'w-full text-left p-3 rounded-lg border shadow-sm bg-white',
            active ? 'border-gray-900' : 'border-gray-200 hover:border-gray-400',
        ].join(' ')}>
      <div className="flex gap-3">
        <div className="w-[88px] h-[66px] rounded border border-gray-200 bg-gray-50 overflow-hidden shrink-0 grid place-items-center">
          {thumb ? (<img src={thumb} alt="thumb" className="w-full h-full object-cover"/>) : (<span className="text-xs text-gray-400">no img</span>)}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="font-semibold leading-snug line-clamp-2">{ad.label || t("(\uC774\uB984\uC5C6\uC74C)")}</div>

            <div className="flex gap-1 shrink-0">
              <button className="text-xs px-2 py-1 rounded border border-gray-200 hover:border-gray-400" onClick={(e) => {
            e.stopPropagation();
            onMoveUp(ad.id);
        }} title={t("\uC704\uB85C")}>
                ↑
              </button>
              <button className="text-xs px-2 py-1 rounded border border-gray-200 hover:border-gray-400" onClick={(e) => {
            e.stopPropagation();
            onMoveDown(ad.id);
        }} title={t("\uC544\uB798\uB85C")}>
                ↓
              </button>
            </div>
          </div>

          <div className="mt-2 flex flex-wrap gap-2 items-center">
            <Badge tone={statusTone(st)}>{statusLabel(st)}</Badge>
            <Badge>area: {ad.area}</Badge>
            <Badge>{ad.device}</Badge>
            <Badge>{ad.country}</Badge>
            <Badge>order: {ad.order ?? 100}</Badge>
          </div>

          <div className="mt-2 text-xs text-gray-500 break-all">{ad.clickUrl || t("(\uB9C1\uD06C \uC5C6\uC74C)")}</div>

          <div className="mt-2 text-xs text-gray-500">
            start: {fmtDT(ad.startAt)} · end: {fmtDT(ad.endAt)}
          </div>

          <div className="mt-2 flex gap-2">
            <button onClick={(e) => {
            e.stopPropagation();
            onEdit(ad);
        }} className="text-xs px-2 py-1 rounded border border-gray-200 hover:border-gray-400">{t("\uC218\uC815")}</button>
            <button onClick={(e) => {
            e.stopPropagation();
            onDelete(ad);
        }} className="text-xs px-2 py-1 rounded bg-red-500 text-white hover:bg-red-600">{t("\uC0AD\uC81C")}</button>
          </div>
        </div>
      </div>
    </button>);
}
export default function AdList({ items, selectedId, query, onQueryChange, statusFilter, onStatusFilterChange, onSelect, onCreate, onEdit, onDelete, onMoveUp, onMoveDown, }) {
    return (<div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="p-3 border-b border-gray-200 bg-gray-50">
        <button onClick={onCreate} className="w-full rounded-lg border border-gray-300 bg-gray-100 hover:bg-gray-200 px-3 py-3 text-left">
          <div className="font-semibold">{t("+ \uAD11\uACE0 \uB4F1\uB85D")}</div>
          <div className="text-xs text-gray-500 mt-1">{t("\uD074\uB9AD\uD558\uBA74 \uB4F1\uB85D \uBAA8\uB2EC")}</div>
        </button>

        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <input value={query} onChange={(e) => onQueryChange(e.target.value)} placeholder={t("\uAC80\uC0C9: \uC774\uB984/\uB9C1\uD06C/\uAD6D\uAC00")} className="flex-1 px-3 py-2 rounded border border-gray-200 focus:outline-none focus:border-gray-500"/>
          <select value={statusFilter} onChange={(e) => onStatusFilterChange(e.target.value)} className="w-full sm:w-auto px-2 py-2 rounded border border-gray-200 bg-white">
            <option value="all">{t("\uC804\uCCB4")}</option>
            <option value="ACTIVE">{t("\uB178\uCD9C")}</option>
            <option value="SCHEDULED">{t("\uC608\uC57D")}</option>
            <option value="PENDING">{t("\uC608\uC57D\uBCF4\uB958")}</option>
            <option value="EXPIRED">{t("\uB9CC\uB8CC")}</option>
            <option value="DISABLED">{t("\uBE44\uD65C\uC131")}</option>
          </select>
        </div>
      </div>

      <div className="p-3 space-y-2 max-h-[70vh] overflow-auto">
        {items.length === 0 ? (<div className="text-sm text-gray-500 p-6 text-center">{t("\uB4F1\uB85D\uB41C \uAD11\uACE0\uAC00 \uC5C6\uC5B4.")}</div>) : (items.map((ad) => (<AdCard key={ad.id} ad={ad} active={ad.id === selectedId} onSelect={onSelect} onEdit={onEdit} onDelete={onDelete} onMoveUp={onMoveUp} onMoveDown={onMoveDown}/>)))}
      </div>
    </div>);
}
